/**
 * PorterOrb
 *
 * Porter's likeness: a slowly turning glass sphere. It ships as an animated
 * WebP rather than a video — an <img> needs no autoplay permission, no
 * `playsinline`, and no poster juggling, and animated WebP is supported by
 * every browser the app targets.
 *
 * The animation is decorative, so it is swapped for a still frame under
 * `prefers-reduced-motion` rather than merely paused.
 *
 * The source is cropped to the sphere's own edge and carries no alpha — the
 * circle is `rounded-full` here. Masking it in the file instead doubled the
 * bytes for the same picture.
 */

import type React from "react";
import { useSyncExternalStore } from "react";
import porterOrbAnimated from "@frontend/shared/assets/images/porter-orb.webp";
import porterOrbStill from "@frontend/shared/assets/images/porter-orb-still.webp";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * The media query is an external store, so it is read through
 * `useSyncExternalStore` rather than mirrored into state from an effect —
 * `react-hooks/set-state-in-effect` rejects the mirror, and this needs no
 * first-render write to settle.
 *
 * Both fallbacks report `true`: the still frame is the safe picture when the
 * query cannot be asked (jsdom, or a render before hydration).
 */
function subscribeToReducedMotion(onChange: () => void): () => void {
  if (typeof window.matchMedia !== "function") {
    return (): void => undefined;
  }
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener("change", onChange);
  return (): void => {
    query.removeEventListener("change", onChange);
  };
}

function readReducedMotion(): boolean {
  if (typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

export interface PorterOrbProps {
  readonly className?: string;
}

export function PorterOrb({
  className = "h-11 w-11",
}: PorterOrbProps): React.JSX.Element {
  const prefersReducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    readReducedMotion,
    (): boolean => true,
  );

  return (
    <img
      src={prefersReducedMotion ? porterOrbStill : porterOrbAnimated}
      alt=""
      aria-hidden="true"
      className={`shrink-0 rounded-full object-cover ${className}`}
    />
  );
}
